import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useDarkMode from "../../../../hooks/useDarkMode";
import useThemeColor from "../../../../hooks/useThemeColor";
import axiosInstance from "../../../../config/axiosApi/axiosApi";
import APIs from "../../../../Constants/apis";
import CustomModal from "../customModal";
import showErrorToast from "../../toast/toast";
import useDebouncedEffect from "../../../../hooks/useDebouncedEffect";

export const normalizeMarketingCustomer = (row) => ({
  cust_Id: row.cust_Id ?? row.Cust_Id,
  cust_Guid: row.cust_Guid ?? row.Cust_Guid ?? "",
  cust_Fname: row.cust_Fname ?? row.Cust_Fname ?? "",
  cust_Lname: row.cust_Lname ?? row.Cust_Lname ?? "",
  cust_Email: row.cust_Email ?? row.Cust_Email ?? "",
  cust_Mobile: row.cust_Mobile ?? row.Cust_Mobile ?? "",
});

export const getMarketingCustomerId = (customer) =>
  Number(customer?.cust_Id ?? customer?.Cust_Id ?? 0);

export const getMarketingCustomerGuid = (customer) =>
  String(customer?.cust_Guid ?? customer?.Cust_Guid ?? "").trim();

export const getMarketingCustomerKey = (customer) => {
  const guid = getMarketingCustomerGuid(customer);
  if (guid) return `g:${guid.toLowerCase()}`;
  const id = getMarketingCustomerId(customer);
  return id > 0 ? `i:${id}` : "";
};

export const marketingCustomerDisplayName = (customer) => {
  const name = `${customer?.cust_Fname || ""} ${customer?.cust_Lname || ""}`.trim();
  return name || "—";
};

function CustomerSelectionModal({
  showModal,
  setShowModal,
  selectedCustomers,
  setSelectedCustomers,
  marketingGroupId = 0,
  onSubmit,
}) {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [localSelected, setLocalSelected] = useState([]);
  const pageSize = 50;
  const fetchSeqRef = useRef(0);

  const safeSelected = Array.isArray(selectedCustomers) ? selectedCustomers : [];

  const fetchCustomers = useCallback(async () => {
    if (!CompID) return;
    const fetchSeq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_CUSTOMERS_FOR_MARKETING_GROUP_PICKER,
        {
          params: {
            CompID,
            MarketingGroup_ID: marketingGroupId,
            Search: searchTerm?.trim() || "",
            SortBy: "Cust_Fname",
            SortOrder: "ASC",
            PageNumber: pageNumber,
            PageSize: pageSize,
          },
        }
      );
      if (fetchSeq !== fetchSeqRef.current) return;
      const raw = response.data?.data ?? [];
      setCustomers((Array.isArray(raw) ? raw : []).map(normalizeMarketingCustomer));
      setTotalRecords(response.data?.totalRecords ?? 0);
    } catch {
      if (fetchSeq !== fetchSeqRef.current) return;
      setCustomers([]);
      showErrorToast("Failed to load customers.");
    } finally {
      if (fetchSeq === fetchSeqRef.current) setLoading(false);
    }
  }, [CompID, marketingGroupId, searchTerm, pageNumber]);

  useEffect(() => {
    if (showModal) {
      setLocalSelected(safeSelected.map(normalizeMarketingCustomer));
      setPageNumber(1);
    }
  }, [showModal]);

  useDebouncedEffect(() => {
    if (showModal) setPageNumber(1);
  }, [searchTerm]);

  useDebouncedEffect(() => {
    if (showModal) fetchCustomers();
  }, [showModal, fetchCustomers]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const isCustomerSelected = (customer) =>
    localSelected.some(
      (c) => getMarketingCustomerKey(c) === getMarketingCustomerKey(customer)
    );

  const toggleCustomer = (customer) => {
    const key = getMarketingCustomerKey(customer);
    setLocalSelected((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      if (safePrev.some((c) => getMarketingCustomerKey(c) === key)) {
        return safePrev.filter((c) => getMarketingCustomerKey(c) !== key);
      }
      return [...safePrev, normalizeMarketingCustomer(customer)];
    });
  };

  const handleSubmit = () => {
    const normalized = (Array.isArray(localSelected) ? localSelected : []).map(
      normalizeMarketingCustomer
    );
    setSelectedCustomers(normalized);
    if (typeof onSubmit === "function") onSubmit(normalized);
    setShowModal(false);
    setSearchTerm("");
  };

  const handleClose = () => {
    setShowModal(false);
    setSearchTerm("");
    setLocalSelected(safeSelected.map(normalizeMarketingCustomer));
  };

  const renderCustomerCard = (customer) => {
    const isSelected = isCustomerSelected(customer);
    const subtitle = [customer.cust_Email, customer.cust_Mobile]
      .filter(Boolean)
      .join(" · ");

    return (
      <div
        key={getMarketingCustomerKey(customer) || getMarketingCustomerId(customer)}
        role="button"
        tabIndex={0}
        onClick={() => toggleCustomer(customer)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleCustomer(customer);
          }
        }}
        style={{
          background: isSelected
            ? isDarkMode
              ? `${themeColor}20`
              : `${themeColor}15`
            : isDarkMode
              ? "#404040"
              : "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: isSelected
            ? `2px solid ${themeColor}`
            : isDarkMode
              ? "2px solid #555"
              : "2px solid #e9ecef",
          transition: "all 0.2s ease",
          cursor: "pointer",
          marginBottom: "12px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span
              style={{
                fontSize: "15px",
                fontWeight: "500",
                color: isSelected
                  ? themeColor
                  : isDarkMode
                    ? "#ffffff"
                    : "#495057",
              }}
            >
              {marketingCustomerDisplayName(customer)}
            </span>
            {subtitle && (
              <small
                style={{
                  color: isSelected
                    ? themeColor
                    : isDarkMode
                      ? "#adb5bd"
                      : "#6c757d",
                  fontSize: "12px",
                  display: "block",
                }}
              >
                {subtitle}
              </small>
            )}
          </div>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "6px",
              border: isSelected
                ? `2px solid ${themeColor}`
                : isDarkMode
                  ? "2px solid #666"
                  : "2px solid #e9ecef",
              background: isSelected ? themeColor : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isSelected && (
              <i className="fa fa-check" style={{ fontSize: "12px", color: "white" }} />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={showModal}
      title="Select Customers"
      subtitle={`${localSelected.length} customer${localSelected.length === 1 ? "" : "s"} selected`}
      onClose={handleClose}
      onSubmit={handleSubmit}
      showButton={true}
      submitButtonText="Select"
      isCentered={true}
      modalSize="modal-lg"
      maxHeight="650px"
    >
      <div className="mb-3">
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: isDarkMode ? "#ffffff" : "#6c757d",
            }}
          >
            <i className="fa fa-search" />
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Name, email, or mobile"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              paddingLeft: "48px",
              borderRadius: "12px",
              height: "48px",
              background: isDarkMode ? "#2d3436" : "#f8f9fa",
              color: isDarkMode ? "#ffffff" : undefined,
              border: isDarkMode ? "2px solid #555" : "2px solid #e9ecef",
            }}
          />
        </div>
      </div>

      <div style={{ minHeight: "280px", maxHeight: "400px", overflowY: "auto" }}>
        {loading && (
          <p className="text-center text-muted py-4">Loading customers...</p>
        )}
        {!loading && customers.length === 0 && (
          <p className="text-center text-muted py-4">No customers found.</p>
        )}
        {!loading && customers.map((customer) => renderCustomerCard(customer))}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={pageNumber <= 1 || loading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="small text-muted">
            Page {pageNumber} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={pageNumber >= totalPages || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </CustomModal>
  );
}

export default CustomerSelectionModal;
